'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';

import type { AccountAddressesSuccessPartialResponse } from '@libs/common-next';
import { phoneFormat } from '@libs/core';

import type { AddressFormInterface } from '../AddressForm/types';
import { addressesPageSchema } from './schema';
import type { AddressesPageInterface } from './types';

export const initialAddressFormState: AddressFormInterface = {
    streetAddress: '',
    apt: '',
    zipCode: '',
    state: '',
    city: '',
};

export const getInitialFormState = (address: AccountAddressesSuccessPartialResponse, isPrimaryAddress: boolean) =>
    address
        ? {
              firstName: (address.firstName ?? '').trim(),
              lastName: (address.lastName ?? '').trim(),
              address: {
                  streetAddress: address.streetLine1 ?? '',
                  apt: address.streetLine2 ?? '',
                  zipCode: address.zipPostalCode ?? '',
                  state: address.stateProvinceAbbreviation ?? '',
                  city: address.city ?? '',
              },
              phone: phoneFormat(address.phoneNumber) ?? '',
              petPreferences: {},
              isPrimary:
                  'isPrimary' in address && typeof address.isPrimary === 'boolean'
                      ? address.isPrimary
                      : isPrimaryAddress,
              useForAllShipments: false,
          }
        : {
              firstName: '',
              lastName: '',
              address: { ...initialAddressFormState },
              phone: '',
              petPreferences: {},
              isPrimary: isPrimaryAddress,
              useForAllShipments: false,
          };

type AddressFormTarget = NonNullable<AccountAddressesSuccessPartialResponse> & { addressId?: number };

type UseAddressFormParams = {
    currentAddressToEdit: AddressFormTarget | null;
    isPrimaryAddress: boolean;
};

const getAddressFormTargetKey = (address: AddressFormTarget | null): string =>
    address?.addressId != null ? String(address.addressId) : 'new';

export const useAddressForm = ({
    currentAddressToEdit,
    isPrimaryAddress,
}: UseAddressFormParams): UseFormReturn<AddressesPageInterface> => {
    const formTargetKey = useMemo(() => getAddressFormTargetKey(currentAddressToEdit), [currentAddressToEdit]);
    const initialFormState = useMemo(
        () => getInitialFormState(currentAddressToEdit, isPrimaryAddress),
        [formTargetKey]
    );

    const methods = useForm<AddressesPageInterface>({
        defaultValues: initialFormState,
        resolver: zodResolver(addressesPageSchema),
        mode: 'onBlur',
    });
    const { reset, setValue } = methods;
    const previousFormTargetKeyRef = useRef(formTargetKey);

    useEffect(() => {
        if (previousFormTargetKeyRef.current === formTargetKey) {
            return;
        }
        previousFormTargetKeyRef.current = formTargetKey;
        reset(getInitialFormState(currentAddressToEdit, isPrimaryAddress));
    }, [formTargetKey, currentAddressToEdit, isPrimaryAddress, reset]);

    useEffect(() => {
        if (formTargetKey !== 'new') {
            return;
        }
        setValue('isPrimary', isPrimaryAddress, { shouldDirty: false });
    }, [formTargetKey, isPrimaryAddress, setValue]);

    return methods;
};
